import { action, makeObservable, reaction } from 'mobx';
import AppStore from './AppStore';
import DominioStore from './DominioStore';
import ErrorStore from './ErrorStore';
import FotoMagistradoStore from './FotoMagistradoStore';
import LogStore from './LogStore';
import MensagemStore from './MensagemStore';
import ParametroStore from './ParametroStore';
import PautaJulgamentoStore from './PautaJulgamentoStore';
import RouterStore from './RouterStore';
import SalaSessaoStore from './SalaSessaoStore';
import SessaoStore from './SessaoStore';
import UsuarioStore from './UsuarioStore';
import PreSessaoStore from './PreSessaoStore';

/**
 * Store raiz do sistema que guarda todas as demais stores e gerencia os estados de forma global
 */
class RootStore {
    disposers = [];

    appStore;
    mensagemStore;
    errorStore;
    parametroStore;
    dominioStore;
    usuarioStore;
    fotoMagistradoStore;
    sessaoStore;
    salaSessaoStore;
    pautaJulgamentoStore;
    preSessaoStore;

    constructor(routingStore) {
        makeObservable(this, {
            disposeReactions: action,
            disposeStoresReactions: action
        });

        this.routerStore = new RouterStore(this, routingStore);
        this.appStore = new AppStore(this);
        this.mensagemStore = new MensagemStore(this);
        this.errorStore = new ErrorStore(this);
        this.logStore = new LogStore(this);
        this.parametroStore = new ParametroStore(this);
        this.dominioStore = new DominioStore(this);
        this.usuarioStore = new UsuarioStore(this);
        this.fotoMagistradoStore = new FotoMagistradoStore(this);
        this.sessaoStore = new SessaoStore(this);
        this.salaSessaoStore = new SalaSessaoStore(this);
        this.pautaJulgamentoStore = new PautaJulgamentoStore(this);
        this.preSessaoStore = new PreSessaoStore(this);

        // Reactions utilizadas para guardar automaticamente informações das stores no localstorage
        this.createReaction(
            reaction(
                () => this.routerStore.objetoLocalStorage,
                (objeto) => this.routerStore.save(objeto)
            )
        );
        this.createReaction(
            reaction(
                () => this.logStore.objetoLocalStorage,
                (objeto) => this.logStore.save(objeto)
            )
        );
        this.createReaction(
            reaction(
                () => this.usuarioStore.objetoLocalStorage,
                (objeto) => this.usuarioStore.save(objeto)
            )
        );
        this.createReaction(
            reaction(
                () => this.sessaoStore.objetoLocalStorage,
                (objeto) => this.sessaoStore.save(objeto)
            )
        );
        this.createReaction(
            reaction(
                () => this.salaSessaoStore.objetoLocalStorage,
                (objeto) => this.salaSessaoStore.save(objeto)
            )
        );
        this.createReaction(
            reaction(
                () => this.pautaJulgamentoStore.objetoLocalStorage,
                (objeto) => this.pautaJulgamentoStore.save(objeto)
            )
        );
        this.createReaction(
            reaction(
                () => this.preSessaoStore.objetoLocalStorage,
                (objeto) => this.preSessaoStore.save(objeto)
            )
        );
    }

    createReaction(disposer) {
        if (process.env.REACT_APP_ENV === 'development') {
            this.disposers.push(() => disposer);
        } else {
            this.disposers.push(disposer);
        }
        return disposer;
    }

    /**
     * Inicializa as stores necessárias antes do login.
     */
    async inicializarStoresSistema() {
        await this.parametroStore.inicializar();
        this.logStore.inicializar();
    }

    /**
     * Inicializa as stores que necessitam de token de autenticação e localização.
     *
     * @see {@link App#iniciarLoginLogus}
     */
    async inicializarStoresUsuario() {
        this.routerStore.inicializar();
        await this.dominioStore.inicializar();
        this.sessaoStore.inicializar();
        this.salaSessaoStore.inicializar();
        this.pautaJulgamentoStore.inicializar();
        this.preSessaoStore.inicializar();
        this.fotoMagistradoStore.inicializar();
    }

    limparStores() {
        this.errorStore.reset(); // Limpa qualquer erro que tenha ficado pra trás
        this.usuarioStore.reset(); // UsuarioStore tem que ser a última a ser limpa
    }

    disposeReactions() {
        this.disposers.forEach((disposer) => {
            if (disposer) {
                disposer();
            }
        });
        this.disposers = [];
    }

    disposeStoresReactions() {
        this.disposeReactions();
        this.routerStore.disposeReactions();
        this.appStore.disposeReactions();
        this.mensagemStore.disposeReactions();
        this.errorStore.disposeReactions();
        this.logStore.disposeReactions();
        this.parametroStore.disposeReactions();
        this.dominioStore.disposeReactions();
        this.usuarioStore.disposeReactions();
        this.sessaoStore.disposeReactions();
        this.salaSessaoStore.disposeReactions();
        this.pautaJulgamentoStore.disposeReactions();
        this.preSessaoStore.disposeReactions();
    }
}

export default RootStore;
